import type {
  TrackerConfig,
  TrackerInitOptions,
  TrackingPolicyBasis,
  TrackingPrivacyMode,
  TrackingRegion
} from './contracts';
import { parseLinkedDomain } from './linker';

const REGION_ENDPOINTS: Record<TrackingRegion, string> = {
  US: 'https://ing.uplane.com',
  EU: 'https://eu.ing.uplane.com'
};
const DEFAULT_FLUSH_INTERVAL_MS = 5_000;
const DEFAULT_SESSION_TIMEOUT_MS = 30 * 60 * 1_000;

const parseBoolean = (value: string | null, fallback: boolean): boolean => {
  if (value === null) return fallback;
  return value === '' || value.toLowerCase() === 'true';
};

const parseInteger = (value: string | null, fallback: number): number => {
  if (value === null) return fallback;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeEndpoint = (value: string): string => {
  const endpoint = new URL(value, window.location.href);
  if (!['http:', 'https:'].includes(endpoint.protocol)) throw new Error('endpoint must use http or https');
  return endpoint.toString().replace(/\/$/, '');
};

const validateWorkspaceId = (workspaceId: string): string => {
  const normalized = workspaceId.trim();
  if (!normalized) throw new Error('workspaceId is required');
  if (normalized.length > 200) throw new Error('workspaceId must not exceed 200 characters');
  return normalized;
};

const parseTrackingRegion = (value: unknown): TrackingRegion | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase();
  return normalized === 'US' || normalized === 'EU' ? normalized : null;
};

const validateTrackingRegion = (value: unknown): TrackingRegion => {
  const region = parseTrackingRegion(value);
  if (!region) throw new Error('region must be US or EU');
  return region;
};

const parsePrivacyMode = (value: unknown): TrackingPrivacyMode | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  return normalized === 'anonymous' || normalized === 'persistent' ? normalized : null;
};

const validatePrivacyMode = (value: unknown): TrackingPrivacyMode => {
  if (value === undefined) return 'anonymous';
  const privacyMode = parsePrivacyMode(value);
  if (!privacyMode) throw new Error('privacyMode must be anonymous or persistent');
  return privacyMode;
};

const parsePolicyBasis = (value: unknown): TrackingPolicyBasis | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  return normalized === 'notice_and_opt_out' || normalized === 'opt_in' ? normalized : null;
};

const validatePolicyBasis = (value: unknown): TrackingPolicyBasis => {
  if (value === undefined) return 'opt_in';
  const policyBasis = parsePolicyBasis(value);
  if (!policyBasis) throw new Error('policyBasis must be notice_and_opt_out or opt_in');
  return policyBasis;
};

const validateLinkedDomains = (value: unknown): string[] => {
  if (value === undefined) return [];
  if (!Array.isArray(value)) throw new Error('linkedDomains must be an array of hostnames');
  return value.map((entry: unknown) => {
    const hostname = parseLinkedDomain(entry);
    if (!hostname) throw new Error('linkedDomains must contain hostnames or *.domain rules without scheme, port or path');
    return hostname;
  });
};

const validateLinkAnonymousSessions = (value: unknown): boolean => {
  if (value === undefined) return true;
  if (typeof value !== 'boolean') throw new Error('linkAnonymousSessions must be a boolean');
  return value;
};

export const validateConfig = (options: TrackerInitOptions): TrackerConfig => {
  const region = validateTrackingRegion(options.region);
  const privacyMode = validatePrivacyMode(options.privacyMode);
  const policyBasis = validatePolicyBasis(options.policyBasis);
  return {
    workspaceId: validateWorkspaceId(options.workspaceId),
    region,
    endpoint: normalizeEndpoint(options.endpoint ?? REGION_ENDPOINTS[region]),
    debugOn: isDebugMode(),
    respectDoNotTrack: options.respectDoNotTrack ?? false,
    privacyMode,
    policyBasis,
    flushIntervalMs: Math.min(Math.max(options.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS, 1_000), 60_000),
    sessionTimeoutMs: Math.min(Math.max(options.sessionTimeoutMs ?? DEFAULT_SESSION_TIMEOUT_MS, 60_000), 24 * 60 * 60 * 1_000),
    linkedDomains: validateLinkedDomains(options.linkedDomains),
    linkAnonymousSessions: validateLinkAnonymousSessions(options.linkAnonymousSessions)
  };
};

export const isDebugMode = (): boolean =>
  new URLSearchParams(window.location.search).get('uplanedebug') === 'true';

export const findTrackerScript = (): HTMLScriptElement | null => {
  if (document.currentScript instanceof HTMLScriptElement) return document.currentScript;
  return document.querySelector<HTMLScriptElement>('script[data-workspace-id][src]');
};

export const readScriptOptions = (script: HTMLScriptElement): TrackerInitOptions | null => {
  const workspaceId = script.dataset.workspaceId;
  const region = script.dataset.region ? parseTrackingRegion(script.dataset.region) : null;
  const privacyMode = script.dataset.privacyMode
    ? parsePrivacyMode(script.dataset.privacyMode)
    : undefined;
  const policyBasis = script.dataset.policyBasis
    ? parsePolicyBasis(script.dataset.policyBasis)
    : undefined;
  if (!workspaceId || !region || (script.dataset.privacyMode && !privacyMode)) return null;
  if (script.dataset.policyBasis && !policyBasis) return null;
  const linkedDomains = script.dataset.linkedDomains
    ?.split(',')
    .map(entry => entry.trim())
    .filter(Boolean);

  return {
    workspaceId,
    region,
    ...(privacyMode ? { privacyMode } : {}),
    ...(policyBasis ? { policyBasis } : {}),
    respectDoNotTrack: parseBoolean(script.dataset.respectDnt ?? null, false),
    flushIntervalMs: parseInteger(script.dataset.flushInterval ?? null, DEFAULT_FLUSH_INTERVAL_MS),
    sessionTimeoutMs: parseInteger(script.dataset.sessionTimeout ?? null, DEFAULT_SESSION_TIMEOUT_MS),
    ...(linkedDomains ? { linkedDomains } : {}),
    linkAnonymousSessions: parseBoolean(script.dataset.linkAnonymousSessions ?? null, true)
  };
};
